import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import session from 'containers/App/session';
import * as actions from '../../../actions';
import * as selectors from '../../../selectors';

import { Form, Modal, Alert } from 'antd';
import Button from 'components/forms/Button';
import NewSaveForm from './NewSaveForm';
import SaveOverwriteForm from './SaveOverwriteForm';
import { FormattedMessage } from 'react-intl';
import messages from '../../../messages';

const SaveModal = ({ visible, formType, onClose, extractStateToSave }) => {
    if (!visible) return null;
    const dispatch = useDispatch();
    const [newSaveFormRef] = Form.useForm();
    const [updateSaveFormRef] = Form.useForm();
    const propertyId = useSelector(session.selectors.selectSelectedPropertyId);
    const userUuid = useSelector(session.selectors.selectUserDetails).uuid;
    const activeSave = useSelector(selectors.selectCurrentlyLoadedSaveDetails);
    const [selectedSaveID, setSelectedSaveID] = useState(activeSave && activeSave.id);
    const saveData = useSelector((state) => extractStateToSave(state.formData));
    const [activeMode, setActiveMode] = useState(activeSave ? 'update' : 'create');
    const updateState = useSelector((state) => selectors.selectFormSavingState(state, formType));
    const fetchingState = useSelector((state) => selectors.selectFetchedSavesState(state, formType));

    const fetchSaves = () => {
        dispatch(actions.fetchSaves(formType, propertyId, userUuid));
    };

    useEffect(() => {
        fetchSaves();
    }, []);

    const resetAndCloseModal = () => {
        dispatch(actions.resetFormSavingState());
        onClose();
    };

    const setModeToCreation = () => {
        setActiveMode('create');
    };
    const setModeToUpdate = () => {
        setActiveMode('update');
    };
    const createOrUpdateSave = async () => {
        let formRef = newSaveFormRef;
        if (activeMode === 'update') formRef = updateSaveFormRef;
        try {
            const { name, description } = await formRef.validateFields();
            const saveDetails = {
                data: saveData,
                name,
                description: description || undefined,
                draft: false,
            };
            if (activeMode === 'update') {
                dispatch(actions.updateSave(selectedSaveID, saveDetails));
            } else {
                dispatch(actions.createSave(formType, propertyId, saveDetails));
            }
        } catch {
            // eslint-disable-next-line no-unused-expressions, prettier/prettier
            () => { };
        }
    };

    /**
     * This handles showing loading state and errors in the modal and only closing it following a successful loading of the save
     * by keeping track of the previous state
     */
    const [prevState, setPrevState] = useState(updateState);
    useEffect(() => {
        if (updateState.fetching === false && prevState.fetching && !updateState.error) {
            resetAndCloseModal();
        }
        setPrevState(updateState);
    }, [updateState, prevState]);

    return (
        <Modal
            centered
            open
            title={<FormattedMessage {...messages.saveYourWork} />}
            footer={[
                <Button key="cancelBtn" onClick={resetAndCloseModal} disabled={updateState.fetching}>
                    <FormattedMessage {...messages.cancelButton} />
                </Button>,
                <Button key="confirmBtn" type="primary" onClick={createOrUpdateSave} loading={updateState.fetching}>
                    {activeMode === 'create' ? (
                        <FormattedMessage {...messages.createSave} />
                    ) : (
                        <FormattedMessage {...messages.updateSave} />
                    )}
                </Button>,
            ]}
            width={1000}
            maskClosable={false}
            closable={false}
        >
            {activeMode === 'create' ? (
                <NewSaveForm formRef={newSaveFormRef} onChangeMode={setModeToUpdate} />
            ) : (
                <SaveOverwriteForm
                    formRef={updateSaveFormRef}
                    activeSave={activeSave}
                    savesList={fetchingState}
                    onRefreshSavesList={fetchSaves}
                    selectedSaveID={selectedSaveID}
                    onChangeSelectedSaveID={setSelectedSaveID}
                    onChangeMode={setModeToCreation}
                />
            )}

            {updateState.error && (
                <Alert
                    style={{ margin: '2em' }}
                    type="error"
                    message={<FormattedMessage {...messages.alertMessage} />}
                    description={
                        <>
                            <FormattedMessage {...messages.saveError} />
                            <Button
                                name="retryFetchButton"
                                onClick={createOrUpdateSave}
                                size="small"
                                style={{ marginLeft: '0.8em' }}
                            >
                                <FormattedMessage {...messages.retryButton} />
                            </Button>
                        </>
                    }
                    showIcon
                />
            )}
        </Modal>
    );
};

export default SaveModal;
